import {
  Autocomplete,
  Divider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  apiClient,
  LEFT_SIDE_VARIANT_ATTRIBUTE_VALUE,
  RIGHT_SIDE_VARIANT_ATTRIBUTE_VALUE,
  SIDE_VARIANT_ATTRIBUTE_NAME,
  StatusEnum,
} from "../../../shared";
import { CustomInputField } from "../../../ui";

export const LensProductVariantSection = ({
  productId,
  productVariantAttributeValue: {
    variantAttribute1,
    variantAttribute1Values,
    variantAttribute2,
    variantAttribute2Values,
    variantAttribute3,
    variantAttribute3Values,
    variantAttribute4,
    variantAttribute4Values,
  },
  fieldNamePrefix,
  header,
}) => {
  const { control, watch, setValue } = useFormContext();

  const productVariant = watch(`${fieldNamePrefix}.productVariant`);
  const enabled = watch(`${fieldNamePrefix}.enabled`);
  const variantAttribute1Value = watch(
    `${fieldNamePrefix}.variantAttribute1Value`
  );
  const variantAttribute2Value = watch(
    `${fieldNamePrefix}.variantAttribute2Value`
  );
  const variantAttribute3Value = watch(
    `${fieldNamePrefix}.variantAttribute3Value`
  );
  const variantAttribute4Value = watch(
    `${fieldNamePrefix}.variantAttribute4Value`
  );

  const [loadingProductVariant, setLoadingProductVariant] = useState(false);
  const [productVariantNotFound, setProductVariantNotFound] = useState(false);

  const fetchProductVariant = useCallback(async () => {
    setValue(`${fieldNamePrefix}.productVariant`, null);
    setLoadingProductVariant(true);
    setProductVariantNotFound(false);
    try {
      const {
        data: { data },
      } = await apiClient.get(`products/${productId}/product-variants`, {
        params: {
          variantAttribute1Value,
          variantAttribute2Value,
          variantAttribute3Value,
          variantAttribute4Value,
        },
      });
      if (data.length > 0) {
        setValue(`${fieldNamePrefix}.productVariant`, data[0]);
      } else {
        setProductVariantNotFound(true);
      }
    } finally {
      setLoadingProductVariant(false);
    }
  }, [
    fieldNamePrefix,
    productId,
    setValue,
    variantAttribute1Value,
    variantAttribute2Value,
    variantAttribute3Value,
    variantAttribute4Value,
  ]);

  useEffect(() => {
    if (variantAttribute1 && !variantAttribute1Value) {
      return;
    }
    if (variantAttribute2 && !variantAttribute2Value) {
      return;
    }
    if (variantAttribute3 && !variantAttribute3Value) {
      return;
    }
    if (variantAttribute4 && !variantAttribute4Value) {
      return;
    }

    fetchProductVariant();
  }, [
    fetchProductVariant,
    variantAttribute1,
    variantAttribute1Value,
    variantAttribute2,
    variantAttribute2Value,
    variantAttribute3,
    variantAttribute3Value,
    variantAttribute4,
    variantAttribute4Value,
  ]);

  // Reset product variant when disabled
  useEffect(() => {
    if (!enabled) {
      setValue(fieldNamePrefix, {
        variantAttribute1Value: null,
        variantAttribute2Value: null,
        variantAttribute3Value: null,
        variantAttribute4Value: null,
        productVariant: null,
      });
    }
  }, [enabled, fieldNamePrefix, setValue]);

  // Auto select the side (if exist)
  useEffect(() => {
    if (variantAttribute4?.name === SIDE_VARIANT_ATTRIBUTE_NAME) {
      const value =
        fieldNamePrefix === "rightSide"
          ? RIGHT_SIDE_VARIANT_ATTRIBUTE_VALUE
          : LEFT_SIDE_VARIANT_ATTRIBUTE_VALUE;
      setValue(`${fieldNamePrefix}.variantAttribute4Value`, value);
    }
  }, [variantAttribute4, fieldNamePrefix, setValue]);

  const renderVariantAttribute = (attribute, attributeValues, index) => {
    // Hide non-exist attribute or Side attribute
    if (!attribute || attribute.name === SIDE_VARIANT_ATTRIBUTE_NAME)
      return null;

    return (
      <Grid size={{ xs: 4 }} key={index}>
        <Controller
          control={control}
          name={`${fieldNamePrefix}.variantAttribute${index + 1}Value`}
          disabled={!watch(`${fieldNamePrefix}.enabled`)}
          render={({ field }) => (
            <Autocomplete
              fullWidth
              value={field.value}
              onChange={(_, newValue) => {
                setValue(
                  `${fieldNamePrefix}.variantAttribute${index + 1}Value`,
                  newValue
                );
              }}
              disableClearable={true}
              options={attributeValues}
              filterOptions={(options, { inputValue }) =>
                options.filter((option) => option === inputValue)
              }
              renderInput={(params) => (
                <CustomInputField
                  {...params}
                  required={true}
                  label={attribute?.name}
                  InputProps={{
                    ...params.slotProps.input,
                    placeholder: "Pilih",
                  }}
                />
              )}
            />
          )}
        />
      </Grid>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Stack direction="row" spacing={2} sx={{
          alignItems: "center"
        }}>
          <Typography variant="body2">{header}</Typography>
          <Controller
            name={`${fieldNamePrefix}.enabled`}
            control={control}
            render={({ field }) => (
              <Switch
                {...field}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </Stack>
      </Grid>
      {watch(`${fieldNamePrefix}.enabled`) && (
        <>
          {renderVariantAttribute(
            variantAttribute1,
            variantAttribute1Values,
            0
          )}
          {renderVariantAttribute(
            variantAttribute2,
            variantAttribute2Values,
            1
          )}
          {renderVariantAttribute(
            variantAttribute3,
            variantAttribute3Values,
            2
          )}
          {renderVariantAttribute(
            variantAttribute4,
            variantAttribute4Values,
            3
          )}
          <Grid size={{ xs: 4 }}>
            <Controller
              control={control}
              name={`${fieldNamePrefix}].axis`}
              render={({ field }) => (
                <CustomInputField
                  value={field.value}
                  onChange={field.onChange}
                  label="Axis"
                  InputProps={{
                    type: "number",
                    placeholder: "Cth: 5",
                  }}
                  inputProps={{ min: 1, max: 360 }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Controller
              control={control}
              name={`${fieldNamePrefix}].pd`}
              render={({ field }) => (
                <CustomInputField
                  value={field.value}
                  onChange={field.onChange}
                  label="PD"
                  InputProps={{
                    type: "number",
                    placeholder: "Cth: 5",
                  }}
                  inputProps={{ min: 1, step: 0.1 }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Controller
              control={control}
              name={`${fieldNamePrefix}].prism`}
              render={({ field }) => (
                <CustomInputField
                  value={field.value}
                  onChange={field.onChange}
                  label="Prism"
                  InputProps={{
                    type: "number",
                    placeholder: "Cth: 5",
                  }}
                  inputProps={{ min: 1, step: 0.1 }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Stack spacing={1.5}>
              {loadingProductVariant && (
                <Typography
                  variant="body2"
                  sx={{
                    fontStyle: "italic",
                    color: "gray",
                  }}
                >
                  Memuat informasi produk varian...
                </Typography>
              )}
              {!productVariant &&
                !loadingProductVariant &&
                !productVariantNotFound && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontStyle: "italic",
                      color: "gray",
                    }}
                  >
                    Pilih atribut varian untuk melihat informasi produk varian
                  </Typography>
                )}
              {productVariantNotFound && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{
                    fontStyle: "italic",
                  }}
                >
                  Ukuran lensa ini tidak tersedia
                </Typography>
              )}
              {productVariant ? (
                productVariant.status === StatusEnum.INACTIVE ? (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{
                      fontStyle: "italic",
                    }}
                  >
                    Barang ini tidak aktif
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    <span style={{ color: "gray" }}>SKU: </span>
                    {productVariant.sku}
                  </Typography>
                )
              ) : null}
            </Stack>
          </Grid>
        </>
      )}
    </Grid>
  );
};

LensProductVariantSection.propTypes = {
  productId: PropTypes.string.isRequired,
  fieldNamePrefix: PropTypes.string.isRequired,
  header: PropTypes.string.isRequired,
  productVariantAttributeValue: PropTypes.object.isRequired,
};
